import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {Button} from "@/components/ui/button.tsx";
import {cn} from "@/lib/utils.ts";
import {Check, ChevronsUpDown} from "lucide-react";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@/components/ui/command.tsx";
import {useEffect, useState} from "react";

export interface ComboboxItem {
  label: string;
  value: string;
}

interface ComboboxFieldProps {
  value?: string | null;
  items: ComboboxItem[];
  name: string;
  searchable?: boolean;
  onSelect: (selectedItem: ComboboxItem | null) => void;
  size?: "sm" | "md" | "lg";
}

const getSizeClass = (size: "sm" | "md" | "lg") => {
  switch (size) {
    case "sm":
      return "w-[150px]"
    case "md":
      return "w-[350px]"
    case "lg":
      return "w-[500px]"
  }
}

export const Combobox = ({name, value, items, onSelect, size, searchable}: ComboboxFieldProps) => {
  size = size || "md"
  const [open, setOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState<string | null>(null)

  const findItem = (value: string) => {
    return items.find((item) => item.value === value)
  }

  useEffect(() => {
    if (value) {
      setSelectedValue(value)
    }
  }, [value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={"outline"} role={"combobox"} aria-expanded={open}
                className={cn(
                  getSizeClass(size),
                  "justify-between",
                  !selectedValue && "text-muted-foreground"
                )}>{selectedValue ? findItem(selectedValue)?.label : "Select " + name}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(getSizeClass(size), "p-0")}>
        <Command>
          {searchable ? (
            <CommandInput placeholder={`Search ${name}...`}/>
          ) : null}
          <CommandList>
            <CommandEmpty>No {name} found.</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  value={item.label}
                  key={item.value}
                  onSelect={(currentValue) => {
                    const selectedItem = items.find((item) => item.label === currentValue)
                    if (!selectedItem) return
                    if (selectedItem.value === selectedValue) {
                      setSelectedValue(null)
                      onSelect(null)
                    } else {
                      setSelectedValue(item.value)
                      onSelect(item)
                    }
                    setOpen(false)
                  }}
                >
                  {item.label}
                  <Check
                    className={cn(
                      "ml-auto",
                      item.value === selectedValue
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
